import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Search, ChevronRight, Share2, Clock, User, Tag } from 'lucide-react';
const categories = ['All', 'Wine Education', 'Pairings', 'Wine Terminology', 'Black-owned Wineries', 'Event Recaps', 'Cocktail Ideas', 'Culinary Culture', 'Hospitality Trends', 'Wine Travel', 'Educational Guides'];
const posts = [{
  id: 1,
  title: 'The Art of the Perfect Pairing: A Summer Guide',
  excerpt: 'Discover the delicate balance between high-acidity whites and seasonal seafood dishes in our latest culinary deep-dive.',
  category: 'Pairings',
  author: 'Treena',
  date: 'June 12, 2026',
  readTime: '6 min read',
  image: 'https://images.unsplash.com/photo-1553830591-2f39e38a013c?auto=format&fit=crop&q=80&w=800',
  featured: true
}, {
  id: 2,
  title: 'Spotlight: Excellence in Black-owned Viticulture',
  excerpt: 'Highlighting the visionary winemakers redefining the industry with bold flavors and sustainable practices.',
  category: 'Black-owned Wineries',
  author: 'Treena',
  date: 'June 10, 2026',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1506377247377-2a5b3b0ca7ef?auto=format&fit=crop&q=80&w=800'
}, {
  id: 3,
  title: 'Understanding Terroir: More Than Just Soil',
  excerpt: 'An educational guide to how climate, topography, and geology shape the unique character of every bottle.',
  category: 'Wine Education',
  author: 'Treena',
  date: 'June 5, 2026',
  readTime: '5 min read',
  image: 'https://images.unsplash.com/photo-1516594915697-87eb3b1c14ea?auto=format&fit=crop&q=80&w=800'
}, {
  id: 4,
  title: 'The Rise of Wine Cocktails in Modern Hospitality',
  excerpt: 'How signature wine-based mixology is creating interactive and entertainment-focused experiences.',
  category: 'Cocktail Ideas',
  author: 'Treena',
  date: 'June 1, 2026',
  readTime: '4 min read',
  image: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&q=80&w=800'
}];
import { motion } from 'framer-motion';
export function Blog() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredPosts = posts.filter(post => (activeCategory === 'All' || post.category === activeCategory) && (post.title.toLowerCase().includes(searchQuery.toLowerCase()) || post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())));
  const featuredPost = posts.find(p => p.featured);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="min-h-screen bg-white" data-id="element-235">
      <Header data-id="element-236" />

      <main className="pt-32 pb-24" data-id="element-237">
        {/* Header & Search */}
        <section className="max-w-7xl mx-auto px-6 mb-16" data-id="element-238">
          <div className="flex flex-col md:flex-row justify-between items-end gap-8 border-b border-[#E6D5B8] pb-12" data-id="element-239">
            <div className="max-w-2xl" data-id="element-240">
              <span className="section-overline" data-id="element-241">The Journal</span>
              <h1 className="text-[#4A0E0E] mb-6" data-id="element-242">Wine Culture & Stories</h1>
              <p className="text-[#2C2C2C]/60" data-id="element-243">
                Explore our curated collection of educational guides, industry
                trends, and culinary inspirations.
              </p>
            </div>
            <div className="relative w-full md:w-80" data-id="element-244">
              <input type="text" placeholder="Search the journal..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-[#FDFCF0] border border-[#E6D5B8] rounded-full px-6 py-3 pl-12 text-sm focus:ring-1 focus:ring-[#4A0E0E] outline-none transition-all" data-id="element-245" />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4A0E0E]/40" size={18} data-id="element-246" />
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="max-w-7xl mx-auto px-6 mb-16 overflow-x-auto no-scrollbar" data-id="element-247">
          <div className="flex space-x-8 whitespace-nowrap pb-4" data-id="element-248">
            {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`text-[10px] uppercase tracking-[0.2em] font-bold transition-all duration-300 pb-2 border-b-2 ${activeCategory === cat ? 'text-[#4A0E0E] border-[#4A0E0E]' : 'text-[#2C2C2C]/40 border-transparent hover:text-[#4A0E0E]'}`} data-id="element-249">
                {cat}
              </button>)}
          </div>
        </section>

        {/* Featured Post */}
        {featuredPost && activeCategory === 'All' && !searchQuery && <section className="max-w-7xl mx-auto px-6 mb-24" data-id="element-250">
            <div className="group cursor-pointer grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-[2rem] overflow-hidden bg-[#FDFCF0] border border-[#E6D5B8]/30 shadow-sm hover:shadow-md transition-all duration-500" data-id="element-251">
              <div className="lg:col-span-7 h-[400px] lg:h-[600px] overflow-hidden" data-id="element-252">
                <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-253" />
              </div>
              <div className="lg:col-span-5 p-12 lg:p-20 flex flex-col justify-center" data-id="element-254">
                <div className="flex items-center gap-4 mb-8 text-[10px] uppercase tracking-widest font-bold text-[#D4AF37]" data-id="element-255">
                  <span data-id="element-256">{featuredPost.category}</span>
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37]/30" data-id="element-257" />
                  <span data-id="element-258">Featured Article</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-serif text-[#4A0E0E] mb-8 leading-tight group-hover:text-[#D4AF37] transition-colors" data-id="element-259">
                  {featuredPost.title}
                </h2>
                <p className="text-[#2C2C2C]/60 mb-10 font-light leading-relaxed text-lg italic" data-id="element-260">
                  "{featuredPost.excerpt}"
                </p>
                <div className="flex items-center justify-between mt-auto pt-8 border-t border-[#E6D5B8]/30" data-id="element-261">
                  <div className="flex items-center gap-3" data-id="element-262">
                    <div className="w-10 h-10 bg-[#4A0E0E] rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-263">
                      T
                    </div>
                    <div className="text-[10px] uppercase tracking-widest" data-id="element-264">
                      <p className="font-bold text-[#4A0E0E]" data-id="element-265">
                        {featuredPost.author}
                      </p>
                      <p className="text-[#2C2C2C]/40" data-id="element-266">{featuredPost.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-[#2C2C2C]/30" data-id="element-267">
                    <Share2 size={18} className="hover:text-[#4A0E0E] transition-colors" data-id="element-268" />
                    <span className="text-[10px] uppercase tracking-widest" data-id="element-269">
                      {featuredPost.readTime}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>}

        {/* Post Grid */}
        <section className="max-w-7xl mx-auto px-6" data-id="element-270">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12" data-id="element-271">
            {filteredPosts.filter(p => !p.featured || activeCategory !== 'All' || searchQuery).map(post => <article key={post.id} className="group cursor-pointer" data-id="element-272">
                  <div className="aspect-[16/10] overflow-hidden rounded-2xl mb-8 shadow-sm" data-id="element-273">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-274" />
                  </div>
                  <div className="flex items-center gap-3 mb-4 text-[10px] uppercase tracking-widest font-bold text-[#D4AF37]" data-id="element-275">
                    <Tag size={12} data-id="element-276" />
                    <span data-id="element-277">{post.category}</span>
                  </div>
                  <h3 className="text-2xl font-serif text-[#4A0E0E] mb-4 group-hover:text-[#D4AF37] transition-colors leading-snug" data-id="element-278">
                    {post.title}
                  </h3>
                  <p className="text-[#2C2C2C]/60 text-sm font-light leading-relaxed mb-8 line-clamp-2" data-id="element-279">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-6 border-t border-[#E6D5B8]/30" data-id="element-280">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#2C2C2C]/40" data-id="element-281">
                      <Clock size={12} data-id="element-282" />
                      <span data-id="element-283">{post.readTime}</span>
                    </div>
                    <button className="text-[10px] uppercase tracking-widest font-bold text-[#4A0E0E] flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-284">
                      Read Article <ChevronRight size={14} data-id="element-285" />
                    </button>
                  </div>
                </article>)}
          </div>

          {filteredPosts.length === 0 && <div className="text-center py-24 bg-[#FDFCF0] rounded-3xl border border-dashed border-[#E6D5B8]" data-id="element-286">
              <p className="text-[#2C2C2C]/40 font-serif italic text-xl" data-id="element-287">
                No articles found in this category yet.
              </p>
            </div>}
        </section>
      </main>

      {/* Newsletter / SEO Footer */}
      <section className="bg-[#4A0E0E] text-white py-24 px-6 mt-24" data-id="element-288">
        <div className="max-w-4xl mx-auto text-center" data-id="element-289">
          <h2 className="text-3xl md:text-5xl font-serif mb-8" data-id="element-290">
            Stay Informed
          </h2>
          <p className="text-white/60 mb-12 font-light text-lg" data-id="element-291">
            Join our inner circle for monthly deep-dives into viticulture,
            exclusive event recaps, and signature cocktail recipes.
          </p>
          <div className="flex flex-col md:flex-row gap-4 max-w-md mx-auto" data-id="element-292">
            <input type="email" placeholder="Email Address" className="bg-white/10 border border-white/20 rounded-full px-8 py-4 text-sm focus:ring-1 focus:ring-[#D4AF37] outline-none w-full" data-id="element-293" />
            <button className="bg-[#D4AF37] text-white px-10 py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-white hover:text-[#4A0E0E] transition-all duration-500" data-id="element-294">
              Subscribe
            </button>
          </div>
        </div>
      </section>

      <Footer data-id="element-295" />
    </motion.div>;
}